import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import SVG from 'src/components/SvgIcon';
import useBindDispatch from 'src/hooks/useBindDispatch';
import {
  setReadingFeedbackVisibility as _setReadingFeedbackVisibility,
  setReadingFeedbackData as _setReadingFeedbackData,
  saveReadingFeedback as _saveReadingFeedback,
  fetchReadingTopics as _fetchReadingTopics,
  resetReadingFeedback as _resetReadingFeedback,
  showPopUpNotification as _showPopUpNotification,
  setReadingFeedbackActiveTab as _setReadingFeedbackActiveTab,
} from 'actions/appActions';
import { sendPostMessageToParent } from 'src/sdk/service/sdk.service';
import { IAppState } from 'src/store/models';
import { DEMO_FOLLOW_UP_OPTIONS } from 'src/redux/sagas/readingFeedback/readingFeedbackSaga';
import { LocalStorage } from 'src/utils/storageHandler';
import { IS_MOBILE_INTEGRATION } from 'constants/constants';

import NewFeedbackTab from './NewFeedbackTab';
import HistoryTab from './HistoryTab';
import styles from './ReadingFeedbackForm.module.scss';

const CLOSE_READING_FEEDBACK = 'closeReadingFeedback';

const normalizeText = (value: string) => value.trim().replace(/\s+/g, ' ');

const ReadingFeedbackForm: React.FC = () => {
  const {
    isFormVisible,
    isLoading,
    isWebViewMode,
    readingTopics,
    chatContext,
    formData,
    activeTab,
    history,
    isHistoryLoading,
  } = useSelector((state: IAppState) => state.app.readingFeedback);

  const setReadingFeedbackVisibility = useBindDispatch(_setReadingFeedbackVisibility);
  const setReadingFeedbackData = useBindDispatch(_setReadingFeedbackData);
  const saveReadingFeedback = useBindDispatch(_saveReadingFeedback);
  const fetchReadingTopics = useBindDispatch(_fetchReadingTopics);
  const resetReadingFeedback = useBindDispatch(_resetReadingFeedback);
  const showPopUpNotification = useBindDispatch(_showPopUpNotification);
  const setReadingFeedbackActiveTab = useBindDispatch(_setReadingFeedbackActiveTab);

  const isOpen = isLoading || isFormVisible;

  useEffect(() => {
    const isMobileIntegration = LocalStorage.getItem(IS_MOBILE_INTEGRATION);

    if (isOpen && isMobileIntegration) {
      const { scrollY } = window;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }

    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (isFormVisible && readingTopics.length === 0) {
      fetchReadingTopics();
    }
  }, [isFormVisible]);

  if (!isFormVisible) return null;

  const handleRating = (rating: 'thumbs_up' | 'thumbs_down') => {
    const newRating = formData.rating === rating ? null : rating;
    const update: any = { rating: newRating };

    if (newRating === 'thumbs_up') {
      if (formData.followUpTimeline === 'no_follow_up') {
        update.followUpTimeline = '';
      }
    } else if (newRating === 'thumbs_down') {
      update.followUpTimeline = 'no_follow_up';
    } else if (newRating === null && formData.rating === 'thumbs_down') {
      update.followUpTimeline = '';
    }

    setReadingFeedbackData(update);
  };

  const followUpOptions = formData.rating === 'thumbs_up'
    ? DEMO_FOLLOW_UP_OPTIONS.filter((opt) => opt.value !== 'no_follow_up')
    : DEMO_FOLLOW_UP_OPTIONS;

  const hasFormData = formData.rating !== null
    || !!formData.memoryClue?.trim()
    || formData.selectedTopics.length > 0
    || !!formData.followUpTimeline?.trim()
    || !!formData.peopleDiscussed?.trim()
    || !!formData.readingComments?.trim();

  const handleTopicToggle = (topic: string) => {
    const current = formData.selectedTopics;
    const updated = current.includes(topic)
      ? current.filter((t) => t !== topic)
      : [...current, topic];
    setReadingFeedbackData({ selectedTopics: updated });
  };

  const handleSave = () => {
    setReadingFeedbackData({
      memoryClue: normalizeText(formData.memoryClue),
      peopleDiscussed: normalizeText(formData.peopleDiscussed),
      readingComments: normalizeText(formData.readingComments),
    });
    saveReadingFeedback();
  };

  const handleCancel = () => {
    if (isWebViewMode) {
      sendPostMessageToParent({ command: CLOSE_READING_FEEDBACK });
    } else {
      setReadingFeedbackVisibility(false);
      resetReadingFeedback();
      showPopUpNotification(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Reading Feedback</span>
            <span className={styles.headerCustomerName}>
              {chatContext?.customerName || ' '}
            </span>
          </div>
          {activeTab === 'new' && (
            <div className={styles.headerRight}>
              <span className={styles.headerTimeLabel}>Reading date and time:</span>
              <span className={styles.headerTime}>
                {chatContext?.readingTime || ''}
              </span>
            </div>
          )}
        </div>

        <div className={styles.tabs}>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.tabActive]: activeTab === 'new' })}
            onClick={() => setReadingFeedbackActiveTab('new')}
          >
            <SVG id="readingFeedbackNewTab" />
            New feedback
          </button>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.tabActive]: activeTab === 'history' })}
            onClick={() => setReadingFeedbackActiveTab('history')}
          >
            <SVG id="readingFeedbackHistoryTab" />
            History
          </button>
        </div>

        <div className={styles.body}>
          <div key={activeTab} className={styles.tabContent}>
            {activeTab === 'history' ? (
              <HistoryTab isLoading={isHistoryLoading} entries={history} />
            ) : (
              <NewFeedbackTab
                formData={formData}
                readingTopics={readingTopics}
                followUpOptions={followUpOptions}
                firstName={chatContext?.firstName}
                onDataChange={setReadingFeedbackData}
                onRatingChange={handleRating}
                onTopicToggle={handleTopicToggle}
              />
            )}
          </div>
        </div>

        {activeTab === 'new' && (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={!hasFormData}
            >
              Save
            </button>
            <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
              cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingFeedbackForm;
